import { NotificationTheme } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import { useTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import React, { FC, memo, useCallback, useEffect } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import {
  logChatbotStatusBannerStatusBannerClicked,
  logChatbotStatusBannerStatusBannerDismissed,
  logChatbotStatusBannerStatusBannerShowed,
} from '../../../bi';
import { headerActions } from '../../../common/reducers/header';
import { selectHideStatusBanner, selectStatusBanner } from '../../../common/selectors/header';
import { renderModel } from '../../../util/renderModel';
import { Banner } from '../Banner';
import s from './StatusBanner.scss';

const isStudioBanner = renderModel.isStudioEditor();

type StatusBannerProps = {
  autoHide?: boolean;
};

export const StatusBanner: FC<StatusBannerProps> = memo(({ autoHide }) => {
  const store = useStore();
  const dispatch = useDispatch();
  const statusBanner = useSelector(selectStatusBanner);
  const hideStatusBanner = useSelector(selectHideStatusBanner);
  const { t } = useTranslation<TLocalesKeys>();

  const onStatusBannerClick = useCallback(
    (e) => {
      if (e.target?.tagName === 'A') {
        const state = store.getState();
        logChatbotStatusBannerStatusBannerClicked(state, statusBanner!.biData);
      }
    },
    [store, statusBanner],
  );

  const onStatusBannerClose = useCallback(() => {
    if (statusBanner) {
      const state = store.getState();
      logChatbotStatusBannerStatusBannerDismissed(state, statusBanner?.biData);
      dispatch(headerActions.resetStatusBanner());
    }
  }, [dispatch, statusBanner]);

  useEffect(() => {
    if (statusBanner) {
      const state = store.getState();
      logChatbotStatusBannerStatusBannerShowed(state, statusBanner.biData);
    }
  }, [statusBanner]);

  if (!statusBanner) {
    return null;
  }

  return (
    <div className={classNames({ [s.studioStatusBanner]: isStudioBanner })}>
      <Banner
        theme="error"
        hideBanner={hideStatusBanner}
        onBannerClose={onStatusBannerClose}
        autoHide={autoHide}
        dataHook="status-banner"
      >
        <span
          className={s.statusBannerContent}
          onClick={onStatusBannerClick}
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t(`status-banner.${statusBanner?.textKey}`, {
                name: statusBanner?.incidentName,
              }),
            ),
          }}
        />
      </Banner>
    </div>
  );
});
